import { getFieldValue, toDate } from "../core/format";
import { isCondition, isLogicalOperator, isNegation, normalizeCondition } from "../core/filter";

const defaultGetGetter = selector =>
  typeof selector === "function" ? selector : item => getFieldValue(item, selector);

function toComparable(value) {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "string") return value.toLowerCase();

  return value;
}

function alignTypes(itemValue, filterValue) {
  if (filterValue instanceof Date && itemValue != null && !(itemValue instanceof Date))
    return [toDate(itemValue), filterValue];

  if (itemValue instanceof Date && filterValue != null && !(filterValue instanceof Date))
    return [itemValue, toDate(filterValue)];

  if (typeof itemValue === "number" && typeof filterValue === "string" && filterValue !== "") {
    const number = Number(filterValue);

    if (!Number.isNaN(number)) return [itemValue, number];
  }

  return [itemValue, filterValue];
}

function compileCondition(condition, getGetter) {
  const [selector, operation, rawFilterValue] = normalizeCondition(condition);
  const getter = getGetter(selector);

  return item => {
    const [value, filterValue] = alignTypes(getter(item), rawFilterValue);
    const left = toComparable(value);
    const right = toComparable(filterValue);

    switch (operation) {
      case "=":
        return left == null || left === "" ? right == null || right === "" : left === right;
      case "<>":
        return left == null || left === "" ? !(right == null || right === "") : left !== right;
      case "<":
        return left != null && right != null && left < right;
      case "<=":
        return left != null && right != null && left <= right;
      case ">":
        return left != null && right != null && left > right;
      case ">=":
        return left != null && right != null && left >= right;
      case "contains":
        return value != null && String(left).includes(String(right ?? ""));
      case "notcontains":
        return value == null || !String(left).includes(String(right ?? ""));
      case "startswith":
        return value != null && String(left).startsWith(String(right ?? ""));
      case "endswith":
        return value != null && String(left).endsWith(String(right ?? ""));
      default:
        return true;
    }
  };
}

export function compileFilter(expression, getGetter = defaultGetGetter) {
  if (expression == null || (Array.isArray(expression) && expression.length === 0))
    return () => true;

  if (typeof expression === "function") return expression;

  if (isCondition(expression)) return compileCondition(expression, getGetter);

  if (isNegation(expression)) {
    const inner = compileFilter(expression[1], getGetter);

    return item => !inner(item);
  }

  const groups = [[]];

  expression.forEach(part => {
    if (isLogicalOperator(part)) {
      if (part.toLowerCase() === "or") groups.push([]);
    } else groups[groups.length - 1].push(compileFilter(part, getGetter));
  });

  return item => groups.some(group => group.every(predicate => predicate(item)));
}

export function compareValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;

  if (typeof a === "string" && typeof b === "string")
    return a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });

  const left = toComparable(a);
  const right = toComparable(b);

  if (left < right) return -1;
  if (left > right) return 1;

  return 0;
}

export function normalizeSortInfo(sort) {
  if (!sort) return [];

  const list = Array.isArray(sort) ? sort : [sort];

  return list
    .map(item => {
      if (typeof item === "string" || typeof item === "function")
        return { selector: item, desc: false };

      return { selector: item.selector, desc: !!item.desc, ...item };
    })
    .filter(item => item.selector != null);
}

export function sortItems(items, sort, getGetter = defaultGetGetter) {
  const info = normalizeSortInfo(sort).map(item => ({ ...item, getter: getGetter(item.selector) }));

  if (info.length === 0) return items.slice();

  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      for (let i = 0; i < info.length; i += 1) {
        const { getter, desc } = info[i];
        const result = compareValues(getter(a.item), getter(b.item));

        if (result !== 0) return desc ? -result : result;
      }

      return a.index - b.index;
    })
    .map(entry => entry.item);
}

function intervalKey(value, groupInterval) {
  if (!groupInterval || value == null) return value;

  if (typeof groupInterval === "number")
    return Math.floor(Number(value) / groupInterval) * groupInterval;

  const date = toDate(value);

  if (!date) return value;

  switch (groupInterval) {
    case "year":
      return date.getFullYear();
    case "month":
      return date.getMonth() + 1;
    case "day":
      return date.getDate();
    case "hour":
      return date.getHours();
    case "minute":
      return date.getMinutes();
    default:
      return value;
  }
}

function groupLevel(items, levels, getGetter) {
  const [level, ...rest] = levels;
  const getter = getGetter(level.selector);
  const groups = [];
  const byKey = new Map();

  items.forEach(item => {
    const key = intervalKey(getter(item), level.groupInterval);
    const comparable = toComparable(key);
    let group = byKey.get(comparable);

    if (!group) {
      group = { key, items: [] };
      byKey.set(comparable, group);
      groups.push(group);
    }

    group.items.push(item);
  });

  groups.sort((a, b) => {
    const result = compareValues(a.key, b.key);

    return level.desc ? -result : result;
  });

  return groups.map(group => {
    const count = group.items.length;

    if (rest.length) {
      const children = groupLevel(group.items, rest, getGetter);

      return { key: group.key, items: level.isExpanded === false ? null : children, count };
    }

    return { key: group.key, items: level.isExpanded === false ? null : group.items, count };
  });
}

export function groupItems(items, group, getGetter = defaultGetGetter) {
  const levels = normalizeSortInfo(group);

  if (levels.length === 0) return items;

  return groupLevel(items, levels, getGetter);
}

export function queryLocal(source, loadOptions = {}, getGetter = defaultGetGetter) {
  const { filter, sort, group, skip, take, requireTotalCount, requireGroupCount } = loadOptions;

  const filtered = source.filter(compileFilter(filter, getGetter));
  const groupInfo = normalizeSortInfo(group);
  const sorted = sortItems(filtered, [...groupInfo, ...normalizeSortInfo(sort)], getGetter);
  const result = {};

  if (groupInfo.length) {
    const groups = groupItems(sorted, groupInfo, getGetter);
    const start = skip || 0;

    result.data = take ? groups.slice(start, start + take) : groups.slice(start);
    if (requireGroupCount) result.groupCount = groups.length;
  } else {
    const start = skip || 0;

    result.data = take ? sorted.slice(start, start + take) : sorted.slice(start);
  }

  if (requireTotalCount) result.totalCount = filtered.length;

  return result;
}
